import { API_BASE } from './api-base';
import { ALFRED_ONBOARDING_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of Alfred's onboarding — `alfredOnboardingInSymphony`.
 *
 * Variant (`true`): the Outreach Alfred row's Connect opens `AlfredSetupModal`. Control (`false`): Connect is
 * the chat handoff, as before the onboarding existed.
 *
 * It FAILS CLOSED to CONTROL, like the sibling gates, and like `alfred-in-outreach-flag` a FAILED read is not
 * cached: control this time, asked again next time, so one slow read does not send every Connect of the page
 * session to chat. A real answer stays cached.
 *
 * Read at the CLICK rather than at render (`AlfredOutreachRow`): a Connect pressed before the gate answered
 * still lands on the arm the release named. The row warms it on mount, so the click normally finds it settled.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, and is inert without a dev-tools
 * grant. The setup endpoints conduct the same toggle themselves, so an override draws the modal and buys
 * nothing the server would not.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function alfredOnboardingEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(ALFRED_ONBOARDING_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/alfred-onboarding`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) {
        cached = null;
        return false;
      }
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control — this time. Not latched.
      cached = null;
      return false;
    }
  })();
  return cached;
}
